/**
 * OperationsSummaryRow — the portal's signature stat-card row, ported
 * into Command Center as slice 3 of the portal→CC merge (ADR-0067).
 *
 * Sits at the top of Operations as a count + jump-to-book bar — and
 * **is the Operations nav** after session 21 (the separate dot+label
 * sub-nav strip was removed when live inspection showed two parallel
 * navigation surfaces stacked, plus the row missed Surface + New Leads
 * cards and read as broken).
 *
 * Renders all 9 books in pipeline order: Surface · New Leads, then the
 * 7 lifecycle phases (Under Review → Rejected). Each card shows its
 * population count and links to its book.
 *
 * Reads everything visual (label, subtitle, accent, route path) from
 * `OPERATIONS_PHASES` — the single source of truth in
 * `lib/operations-phases.ts`. The count comes from
 * `useOperationsSummary()` against `GET /api/operations/summary`.
 * Filtered to `SUMMARY_PHASE_KEYS` (from @command-center/shared) so
 * adding/removing a counted phase is a single-file change.
 *
 * Loading / error: cards render with a `—` count rather than
 * disappearing or shifting layout. The row is a glance — flickering
 * skeletons would be more visual cost than the wait justifies.
 *
 * Spec: docs/port-survey/reconciliation.md (evicted 2026-08-29, #775 round 10) (D2). Visual reference:
 *       docs/port-survey/portal-1b-list.md (evicted 2026-08-29, #775 round 10) (P-LIST stat cards).
 */

import { Link } from "@tanstack/react-router";
import {
  SUMMARY_PHASE_KEYS,
  type SummaryPhaseKey,
} from "@command-center/shared";
import { OPERATIONS_PHASES } from "@/lib/operations-phases";
import { useOperationsSummary } from "@/lib/use-operations-summary";

const SUMMARY_KEY_SET = new Set<string>(SUMMARY_PHASE_KEYS);

/** Format a count for display. `—` is the placeholder for loading +
 *  error states; 0 is a real value and renders as `0`, not `—`. */
function fmtCount(n: number | undefined): string {
  return typeof n === "number" ? String(n) : "—";
}

export function OperationsSummaryRow() {
  const { data, isError } = useOperationsSummary();
  // Iterate in OPERATIONS_PHASES order so the row reads in pipeline order
  // (New Leads · 7 lifecycle phases = the 8 books). The Surface card is
  // dropped (2026-06-20 nav reorg): Surface is its own sidebar tab now, so
  // the book row is the books only. The API may still return a surface count;
  // we simply don't render its card. Filtered by SUMMARY_PHASE_KEYS for
  // forward-compat if a phase is ever excluded.
  const phases = OPERATIONS_PHASES.filter(
    (p) => p.key !== "surface" && SUMMARY_KEY_SET.has(p.key),
  );

  return (
    <div
      // 9 cards: 3 cols on mobile (3 rows), 5 on tablet (2 rows of 5+4),
      // 9 across at lg+. Each card stays readable down to ~120px wide.
      className="grid grid-cols-3 gap-2 border-b bg-muted/30 px-4 py-3 sm:grid-cols-5 lg:grid-cols-9"
      data-testid="operations-summary-row"
    >
      {phases.map((phase) => {
        const count = isError
          ? undefined
          : data?.counts[phase.key as SummaryPhaseKey];
        return (
          <Link
            key={phase.key}
            to={phase.path}
            // The portal's flagship "you are here" (.stat-card.active, ADR-0138):
            // warm wash + orange edge + soft orange glow; the numeral takes the
            // darkened AA orange. Hover = the portal's shadow lift, not a bg wash.
            className="group overflow-hidden rounded-xl border bg-card text-card-foreground px-3.5 py-2.5 text-left shadow-sm transition-[background-color,border-color,box-shadow] duration-150 hover:shadow-lift data-[status=active]:border-active-edge data-[status=active]:bg-active-wash data-[status=active]:shadow-active"
            activeProps={{ "data-status": "active" }}
            data-testid={`operations-summary-card-${phase.key}`}
          >
            <div className="flex flex-col gap-0.5">
              <span
                className="font-serif text-2xl leading-none text-foreground group-data-[status=active]:text-active-fg"
                data-testid={`operations-summary-count-${phase.key}`}
              >
                {fmtCount(count)}
              </span>
              <span className="text-[12px] font-semibold leading-tight">
                {phase.label}
              </span>
              <span className="text-[10px] leading-tight text-muted-foreground">
                {phase.subtitle}
              </span>
            </div>
          </Link>
        );
      })}
    </div>
  );
}
